<nz-spin [nzSpinning]="loading()">
  <form nz-form [formGroup]="form" nzLayout="vertical" (ngSubmit)="submit()" class="space-y-5">

    <!-- Lifecycle status -->
    <div class="flex items-center">
      <nz-tag
        class="m-0"
        [nzColor]="lifecycleColor()"
        nz-tooltip
        [nzTooltipTitle]="lifecycleEditableTooltip()"
      >
        {{ lifecycleLabel() }}
      </nz-tag>
    </div>

    <!-- Customer + current balance -->
    @if (info.customer; as cust) {
      <div class="flex items-end justify-between border-b border-gray-100 pb-3">
        <div>
          <div class="text-xs text-gray-400">{{ 'servicePackages.editModal.customer' | translate }}</div>
          <div class="text-sm font-medium text-gray-900">{{ cust.name || '—' }}</div>
          @if (cust.phone) {
            <div class="text-xs text-gray-500">{{ cust.phone }}</div>
          }
        </div>
        <div class="text-right">
          <div class="text-xs text-gray-400">{{ 'servicePackages.editModal.customerBalance' | translate }}</div>
          <div
            class="text-base font-semibold"
            [class.text-emerald-700]="(cust.balance ?? 0) >= 0"
            [class.text-rose-700]="(cust.balance ?? 0) < 0"
          >
            {{ (cust.balance ?? 0) | number: '1.2-2' }} ₾
          </div>
        </div>
      </div>
    }

    <!-- Purchase summary: flat key/value strip, no heavy box -->
    <div class="grid grid-cols-2 gap-x-6 gap-y-4">
      <div>
        <div class="text-xs text-gray-400">{{ 'servicePackages.editModal.saleDate' | translate }}</div>
        <div class="text-sm text-gray-800">{{ info.purchasedAt | date: 'dd/MM/yyyy' }}</div>
      </div>
      <div>
        <div class="text-xs text-gray-400">{{ 'servicePackages.editModal.soldBy' | translate }}</div>
        <div class="text-sm text-gray-800">{{ info.createdBy?.name || '—' }}</div>
      </div>
      <div>
        <div class="text-xs text-gray-400">{{ 'servicePackages.editModal.validity' | translate }}</div>
        <div class="whitespace-nowrap text-sm text-gray-800">{{ info.validFrom | date: 'dd/MM/yyyy' }} – {{ info.validUntil | date: 'dd/MM/yyyy' }}</div>
      </div>
      <div>
        <div class="text-xs text-gray-400">{{ 'servicePackages.editModal.totalPaid' | translate }}</div>
        <div class="text-sm font-semibold text-gray-900">{{ info.purchasePrice | number: '1.2-2' }} ₾</div>
      </div>
    </div>
    @if (paymentLines().length) {
      <div class="flex flex-wrap items-center gap-2 text-xs text-gray-500">
        <span class="text-gray-400">{{ 'servicePackages.editModal.payment' | translate }}</span>
        @for (line of paymentLines(); track line.label) {
          <span class="rounded bg-gray-100 px-2 py-0.5 text-gray-600">
            {{ line.label | translate }} · {{ line.amount | number: '1.2-2' }} ₾
          </span>
        }
      </div>
    }

    <hr class="border-gray-100" />

    <!-- Editable purchase-level fields -->
    <div class="flex gap-3">
      <div class="flex-1">
        <beautyapp-input
          formControlName="displayName"
          [label]="'servicePackages.editModal.displayName' | translate"
          type="text"
        ></beautyapp-input>
      </div>
      <div class="flex-1">
        <nz-form-item>
          <nz-form-label [nzSpan]="24">{{ 'servicePackages.editModal.validUntil' | translate }}</nz-form-label>
          <nz-form-control [nzSpan]="24">
            <nz-date-picker
              formControlName="validUntil"
              nzFormat="yyyy-MM-dd"
              [nzDisabledDate]="disabledValidUntil"
              class="w-full"
              style="width: 100%"
            ></nz-date-picker>
          </nz-form-control>
        </nz-form-item>
      </div>
    </div>

    <nz-form-item>
      <nz-form-label [nzSpan]="24">{{ 'servicePackages.editModal.comment' | translate }}</nz-form-label>
      <nz-form-control [nzSpan]="24">
        <textarea nz-input formControlName="comment" rows="2"></textarea>
      </nz-form-control>
    </nz-form-item>

    <!-- Services -->
    <div>
      <div class="mb-1 flex items-baseline justify-between">
        <h4 class="m-0 text-xs font-semibold uppercase tracking-wide text-gray-400">
          {{ 'servicePackages.editModal.items' | translate }}
        </h4>
        @if (totals().count) {
          <span class="text-xs text-gray-400">{{ totals().count }}</span>
        }
      </div>

      <!--
        Column header + label-free inputs in a CSS grid (mirrors package-form).
        Inputs sit at the top of each grid cell (items-start), so they stay
        aligned regardless of header wrap, and a field's validation error grows
        downward inside its own cell without shifting its neighbours.
      -->
      @if (form.controls.items.length) {
        <div
          class="grid grid-cols-[minmax(0,1fr)_84px_116px_116px_28px] items-end gap-3 pb-1 text-[11px] font-medium leading-tight text-gray-400"
        >
          <span>{{ 'servicePackages.editModal.service' | translate }}</span>
          <span>{{ 'servicePackages.editModal.quantity' | translate }}</span>
          <span>{{ 'servicePackages.editModal.originalPrice' | translate }}</span>
          <span>{{ 'servicePackages.editModal.discountedPrice' | translate }}</span>
          <span></span>
        </div>
      }

      <div formArrayName="items" class="divide-y divide-gray-100">
        @for (group of form.controls.items.controls; track $index) {
          <div [formGroup]="group" class="py-3 first:pt-1">
            <div class="grid grid-cols-[minmax(0,1fr)_84px_116px_116px_28px] items-start gap-3">
              <beautyapp-select
                class="block min-w-0"
                formControlName="serviceId"
                [data]="servicesCatalog"
                displayMember="name"
                valueMember="id"
              ></beautyapp-select>
              <beautyapp-input
                class="block"
                formControlName="quantity"
                type="number"
              ></beautyapp-input>
              <span
                class="block"
                nz-tooltip
                [nzTooltipTitle]="priceRoleBlocked ? ('servicePackages.editModal.priceRoleDisabled' | translate) : ''"
              >
                <beautyapp-input
                  class="block"
                  formControlName="originalPrice"
                  type="number"
                  prefix="₾"
                ></beautyapp-input>
              </span>
              <span
                class="block"
                nz-tooltip
                [nzTooltipTitle]="priceRoleBlocked ? ('servicePackages.editModal.priceRoleDisabled' | translate) : ''"
              >
                <beautyapp-input
                  class="block"
                  formControlName="discountedPrice"
                  type="number"
                  prefix="₾"
                ></beautyapp-input>
              </span>
              <button
                class="justify-self-center"
                nz-button
                nzType="text"
                nzDanger
                nzSize="small"
                type="button"
                nz-tooltip
                [nzTooltipTitle]="'buttons.remove' | translate"
                (click)="removeItem($index)"
              >
                <span nz-icon nzType="delete" nzTheme="outline"></span>
              </button>
            </div>

            <div class="mt-1 flex items-center gap-3 text-xs text-gray-400">
              <span>{{ 'servicePackages.editModal.consumed' | translate }} {{ itemConsumed($index) }}</span>
              <span class="text-gray-300">·</span>
              <span>{{ 'servicePackages.editModal.remaining' | translate }} {{ remainingFor($index) }}</span>
            </div>

            @if (priceWarning($index); as warn) {
              <div class="mt-1.5 flex items-start gap-1.5 rounded bg-amber-50 px-2 py-1 text-xs text-amber-700">
                <span nz-icon nzType="warning" nzTheme="outline" class="mt-0.5"></span>
                <span>{{ warn }}</span>
              </div>
            }
          </div>
        }
      </div>

      <button nz-button nzType="link" type="button" class="mt-2 inline-flex items-center gap-1 px-0" (click)="addItem()">
        <span nz-icon nzType="plus" nzTheme="outline"></span>
        {{ 'servicePackages.editModal.addItem' | translate }}
      </button>
    </div>

    <!-- Totals -->
    @if (totals().count) {
      <div class="grid grid-cols-2 gap-x-6 gap-y-2 rounded-md bg-gray-50 px-4 py-3 text-sm sm:grid-cols-4">
        <div class="flex items-baseline justify-between sm:block">
          <span class="text-xs text-gray-400">{{ 'servicePackages.editModal.totalServices' | translate }}</span>
          <span class="font-semibold text-gray-900 sm:block">{{ totals().count }}</span>
        </div>
        <div class="flex items-baseline justify-between sm:block">
          <span class="text-xs text-gray-400">{{ 'servicePackages.editModal.totalQuantity' | translate }}</span>
          <span class="font-semibold text-gray-900 sm:block">{{ totals().quantity }}</span>
        </div>
        <div class="flex items-baseline justify-between sm:block">
          <span class="text-xs text-gray-400">{{ 'servicePackages.editModal.totalOriginal' | translate }}</span>
          <span class="font-semibold text-gray-500 line-through sm:block">{{ totals().original | number: '1.2-2' }} ₾</span>
        </div>
        <div class="flex items-baseline justify-between sm:block">
          <span class="text-xs text-gray-400">{{ 'servicePackages.editModal.totalDiscounted' | translate }}</span>
          <span class="font-semibold text-gray-900 sm:block">{{ totals().discounted | number: '1.2-2' }} ₾</span>
        </div>
      </div>
    }

    <!-- Reason (only when required) -->
    @if (reasonRequiredNow()) {
      <div class="space-y-3 rounded-md border border-amber-200 bg-amber-50 p-3">
        <nz-form-item class="m-0">
          <nz-form-label [nzSpan]="24" nzRequired>{{ 'servicePackages.editModal.reason' | translate }}</nz-form-label>
          <nz-form-control [nzSpan]="24">
            <nz-select formControlName="reason" nzPlaceHolder="აირჩიეთ მიზეზი" style="width: 100%">
              @for (opt of reasonOptions; track opt.value) {
                <nz-option [nzValue]="opt.value" [nzLabel]="opt.label"></nz-option>
              }
            </nz-select>
          </nz-form-control>
        </nz-form-item>
        @if (form.controls.reason.value === EditReason.OTHER) {
          <nz-form-item class="m-0">
            <nz-form-label [nzSpan]="24" nzRequired>{{ 'servicePackages.editModal.reasonText' | translate }}</nz-form-label>
            <nz-form-control [nzSpan]="24">
              <textarea nz-input formControlName="reasonText" rows="2" placeholder="დაწერეთ მიზეზი"></textarea>
            </nz-form-control>
          </nz-form-item>
        }
      </div>
    }

    <!-- Price-change settlement -->
    @if (adjustmentPreview(); as adj) {
      <div
        class="rounded-md px-3 py-2.5"
        [class.bg-emerald-50]="adj.sign === '+'"
        [class.bg-rose-50]="adj.sign === '-'"
      >
        <div class="flex items-center justify-between text-sm">
          <span [class.text-emerald-700]="adj.sign === '+'" [class.text-rose-700]="adj.sign === '-'">
            @if (adj.sign === '-') {
              {{ 'servicePackages.editModal.amountToCollect' | translate }}
            } @else {
              {{ 'servicePackages.editModal.refundLabel' | translate }}
            }
          </span>
          <span class="font-semibold" [class.text-emerald-700]="adj.sign === '+'" [class.text-rose-700]="adj.sign === '-'">
            {{ adj.amount | number: '1.2-2' }} ₾
          </span>
        </div>
        @if (adj.sign === '-') {
          <nz-radio-group class="mt-2" formControlName="chargeSettlement">
            <!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -->
            <label nz-radio nzValue="COLLECT">{{ 'servicePackages.editModal.chargeCollect' | translate }}</label>
            <!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -->
            <label nz-radio nzValue="NONE">{{ 'servicePackages.editModal.settlementNone' | translate }}</label>
          </nz-radio-group>
          @if (form.controls.chargeSettlement.value === 'COLLECT') {
            <beautyapp-payment-method-block class="mt-2 block" [group]="form.controls.payment" [amount]="adj.amount"></beautyapp-payment-method-block>
          }
        } @else {
          <nz-radio-group class="mt-2" formControlName="settlementMethod">
            <!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -->
            <label nz-radio nzValue="BALANCE">{{ 'servicePackages.editModal.settlementBalance' | translate }}</label>
            <!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -->
            <label nz-radio nzValue="NONE">{{ 'servicePackages.editModal.settlementNone' | translate }}</label>
          </nz-radio-group>
        }
        <!-- Free-form note on the refund/charge — only when money actually moves -->
        @if (settlementMovesMoney()) {
          <textarea
            nz-input
            class="mt-2"
            rows="2"
            formControlName="settlementComment"
            [placeholder]="'servicePackages.editModal.settlementCommentPlaceholder' | translate"
          ></textarea>
        }
      </div>
    }

    <!-- Edit history: inline timeline -->
    <div class="border-t border-gray-100 pt-3">
      <button
        type="button"
        class="flex w-full items-center gap-2 bg-transparent text-left text-xs font-semibold uppercase tracking-wide text-gray-400"
        (click)="historyOpen.set(!historyOpen())"
      >
        <span nz-icon [nzType]="historyOpen() ? 'down' : 'right'" nzTheme="outline" class="text-[10px]"></span>
        {{ 'servicePackages.editModal.history' | translate }}
        @if (audit().length) {
          <span class="rounded-full bg-gray-100 px-1.5 text-[11px] font-normal text-gray-500">{{ audit().length }}</span>
        }
      </button>

      @if (historyOpen()) {
        @if (audit().length === 0) {
          <p class="mt-3 text-sm text-gray-400">{{ 'servicePackages.editModal.noHistory' | translate }}</p>
        } @else {
          <ol class="mt-3 space-y-3">
            @for (entry of audit(); track entry.id) {
              <li class="flex gap-3">
                <span class="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-gray-300"></span>
                <div class="min-w-0 flex-1">
                  <div class="flex flex-wrap items-baseline gap-x-2 text-sm">
                    <span class="font-medium text-gray-800">{{ auditFieldLabelKey(entry.fieldChanged) | translate }}</span>
                    <span class="text-xs text-gray-400">{{ entry.editedAt | date: 'dd/MM/yyyy HH:mm' }}</span>
                    <span class="text-xs text-gray-400">· {{ entry.editor?.fullName ?? entry.editorId }}</span>
                  </div>
                  <div class="break-words text-xs text-gray-500">
                    <span class="text-gray-400">{{ formatAuditValue(entry.fieldChanged, entry.oldValue) }}</span>
                    <span class="px-1 text-gray-300">→</span>
                    <span class="text-gray-700">{{ formatAuditValue(entry.fieldChanged, entry.newValue) }}</span>
                  </div>
                </div>
              </li>
            }
          </ol>
        }
      }
    </div>

    <!-- Actions -->
    <div class="flex justify-end gap-2 border-t border-gray-100 pt-4">
      <button nz-button type="button" (click)="cancel()">{{ 'buttons.close' | translate }}</button>
      <button nz-button nzType="primary" type="submit" [nzLoading]="loading()" [disabled]="form.invalid">
        {{ 'buttons.save' | translate }}
      </button>
    </div>
  </form>
</nz-spin>
